import { useForm } from 'react-hook-form'
import { Link } from 'react-router'

import { yupResolver } from '@hookform/resolvers/yup'
import { SignupPageStatus } from '@ui/interfaces'
import { useAuth } from '@ui/providers/AuthProvider'
import * as Yup from 'yup'

type SignupFormProps = {
  setPageStatus: (status: SignupPageStatus) => void
  isDemo?: boolean
}

export function SignupForm(props: SignupFormProps) {
  const auth = useAuth()
  const { setPageStatus, isDemo } = props

  const placeholderName = ['Dawson Leery', 'Joey Potter', 'Pacey Witter', 'Jen Lindley'][Math.floor(Math.random() * 4)]

  const schema = Yup.object({
    email: Yup.string().email().required('Email required'),
    name: Yup.string().optional(),
    password: Yup.string()
      .min(12, 'Password is too short - should be 12 characters minimum.')
      .matches(/[0-9]/, 'Password must contain at least one digit.')
      .matches(/[a-z]/, 'Password must contain at least one lowercase letter.')
      .matches(/[A-Z]/, 'Password must contain at least one uppercase letter.')
      .matches(/[^A-Za-z0-9]/, 'Password must contain at least one symbol.')
      .required('Please enter your password'),
    passwordConfirmation: Yup.string()
      .oneOf([Yup.ref('password'), ''], 'Passwords must match')
      .required('Please confirm your password'),
  })

  const {
    register,
    handleSubmit,
    setError,
    formState: { errors },
  } = useForm({
    resolver: yupResolver(schema),
  })

  const sendToServer = ({
    email,
    password,
    passwordConfirmation,
    name,
  }: {
    email: string
    password: string
    passwordConfirmation: string
    name?: string
  }) => {
    const nameSplit = (name || '').split(' ')
    const firstName = nameSplit.shift()
    const lastName = nameSplit.join(' ')
    const credentials = {
      user: {
        email,
        password,
        password_confirmation: passwordConfirmation,
        first_name: firstName === '' ? null : firstName,
        last_name: lastName === '' ? null : lastName,
        is_demo: !!props.isDemo,
      },
    }

    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    return auth.signupWithPassword(credentials, (error: any) => {
      if (error) {
        const { response } = error
        // Error is an object with key and value as strings.

        if (response && response.status === 422 && response.data.errors) {
          const { errors, error } = response.data
          if (error) {
            setError('email', { message: error })
          } else {
            const stringifiedError = Object.keys(errors)
              .map((key) => `${key} ${errors[key]}`)
              .join(', ')

            setError('email', { message: stringifiedError })
          }
        } else {
          // For non-validation errors (e.g., 500 Internal Server Error), you can set a global error
          // This could be a state in your component to show the error message
          setPageStatus(SignupPageStatus.FAIL) // Or any other way you handle global errors
        }
      } else {
        setPageStatus(SignupPageStatus.CONFIRM_EMAIL_NOTIFICATION) // Or any other way you handle global errors
      }
    })
  }

  const messageContent = () => {
    return (
      <div className="hidden md:block">
        {isDemo ? (
          <>
            <h1 className="text-4xl font-semibold text-gray-800">Explore Datable's Sandbox</h1>
            <p className="text-sm text-gray-600">
              Create your free account to dive into the Sandbox environment and experience the power of Datable
              firsthand. Start experimenting with data transformations and see how easy it is to manage your data.{' '}
            </p>
          </>
        ) : (
          <>
            <h1 className="text-4xl font-semibold text-gray-800">Start your 30-day free trial!</h1>
            <p className="text-sm text-gray-600">
              Unlock the full potential of Datable with a 30-day free trial. Experience all features and functionalities
              to transform your data effortlessly.
            </p>
          </>
        )}
      </div>
    )
  }
  return (
    <div className={'flex h-fit w-full flex-col space-y-4 p-8 pt-0 md:p-0 md:pt-8'}>
      <div className="flex w-full flex-col items-center justify-center space-y-2 text-center">
        {messageContent()} <p className="py-2 text-sm font-semibold text-gray-600">Sign up for free</p>
      </div>
      <form onSubmit={handleSubmit(sendToServer)} className="space-y-2 text-left">
        <div>
          <label htmlFor="name" className="text-sm text-gray-700">
            Your name
          </label>
          <input
            {...register('name')}
            type="text"
            name="name"
            id="name"
            className="mt-2 block w-full rounded-md border-0 bg-white text-gray-600 shadow-sm outline-none ring-2 ring-inset ring-gray-200 transition-all duration-300 ease-in-out placeholder:text-gray-500 focus:ring-2 focus:ring-inset focus:ring-primary-200 sm:text-sm sm:leading-6"
            placeholder={placeholderName}
          />
        </div>
        <div>
          <label htmlFor="email" className="text-sm text-gray-700">
            Email
          </label>
          <input
            {...register('email')}
            type="text"
            name="email"
            id="email"
            className="mt-2 block w-full rounded-md border-0 bg-white text-gray-600 shadow-sm outline-none ring-2 ring-inset ring-gray-200 transition-all duration-300 ease-in-out placeholder:text-gray-500 focus:ring-2 focus:ring-inset focus:ring-primary-200 sm:text-sm sm:leading-6"
            placeholder="name@company.com"
          />
          {errors.email && <p className="mb-2 text-xs italic text-red-500">{errors.email?.message}</p>}
        </div>
        <div>
          <label htmlFor="password" className="text-sm text-gray-700">
            Password
          </label>
          <input
            {...register('password')}
            type="password"
            name="password"
            id="password"
            className="mt-2 block w-full rounded-md border-0 bg-white text-gray-600 shadow-sm outline-none ring-2 ring-inset ring-gray-200 transition-all duration-300 ease-in-out placeholder:text-gray-500 focus:ring-2 focus:ring-inset focus:ring-primary-200 sm:text-sm sm:leading-6"
          />
          {errors.password && <p className="mt-2 text-xs italic text-red-500">{errors.password?.message}</p>}
        </div>
        <div>
          <label htmlFor="passwordConfirmation" className="text-sm text-gray-700">
            Confirm password
          </label>
          <input
            {...register('passwordConfirmation')}
            type="password"
            name="passwordConfirmation"
            id="passwordConfirmation"
            className="mt-2 block w-full rounded-md border-0 bg-white text-gray-600 shadow-sm outline-none ring-2 ring-inset ring-gray-200 transition-all duration-300 ease-in-out placeholder:text-gray-500 focus:ring-2 focus:ring-inset focus:ring-primary-200 sm:text-sm sm:leading-6"
          />
          {errors.passwordConfirmation && (
            <p className="mb-2 text-xs italic text-red-500">{errors.passwordConfirmation?.message}</p>
          )}
        </div>
        <button
          type="submit"
          className="w-full rounded-md bg-primary-500 px-3 py-2 text-sm font-semibold text-white shadow-sm transition-colors duration-300 ease-in-out hover:bg-primary-800 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-primary-600">
          Sign up
        </button>

        <div className="space-y-2 text-center text-sm">
          <p>
            By signing up to create an account I accept the{' '}
            <Link
              to="#"
              className="font-medium text-gray-700 transition-all duration-300 ease-in-out hover:font-semibold hover:text-gray-600 hover:underline">
              Terms of Use
            </Link>{' '}
            and{' '}
            <Link
              to="#"
              className="font-medium text-gray-700 transition-all duration-300 ease-in-out hover:font-semibold hover:text-gray-600 hover:underline">
              Privacy Policy
            </Link>
            .
          </p>
          Already have an account?{' '}
          <Link
            to="/login"
            key="login"
            onClick={() => setPageStatus(SignupPageStatus.LOGIN)}
            className="pl-1 text-primary-500">
            Sign in
          </Link>
        </div>
      </form>
    </div>
  )
}
